import type {
  ContactValues,
  TdUnit,
  TdVehicleModel,
  TdVehicleVersion,
} from "./types"

const crmSubmitUrl = (): string =>
  process.env.NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL || ""

interface SubmitArgs {
  accountId?: string
  siteId?: string
  model: TdVehicleModel | null
  version: TdVehicleVersion | null
  unit: TdUnit | null
  date: string
  timeSlot: string
  contact: ContactValues
  afVisitorId?: string
  antiBotToken?: string
  /** Override do endpoint (aba Avançado). Default = NEXT_PUBLIC_DEFAULT_FORM_SUBMIT_URL. */
  endpoint?: string
  /** Override do "Produto" gravado no lead (aba Avançado). Default = nome do modelo. */
  product?: string
}

export interface SubmitResult {
  ok: boolean
  /** protocolo TD-… (só quando o crm cria o booking de forma síncrona). */
  protocol?: string
}

/** Envia o lead do Test Drive para o crm (POST /conversions). O marcador `test_drive`
    vai no TOP-LEVEL do payload — o `.catchall` do createConversionSchema roteia toda chave
    desconhecida para `customParameters`, então ele chega em `customParameters.test_drive`
    (embrulhá-lo num `customParameters` aqui viraria `customParameters.customParameters`).
    O crm converte em agendamento e devolve o protocolo (criação síncrona). */
export const submitTestDrive = async (a: SubmitArgs): Promise<SubmitResult> => {
  const url = a.endpoint?.trim() || crmSubmitUrl()
  if (!url || !a.accountId || !a.siteId || !a.unit) {
    throw new Error("Configuração de envio ausente (crm/conta/site/unidade).")
  }

  const contentEntryId = a.version?.uuid || a.model?.uuid

  const payload: Record<string, unknown> = {
    name: a.contact.name.trim(),
    email: a.contact.email.trim(),
    phone: a.contact.whatsapp,
    ...(a.contact.cpf ? { cpf: a.contact.cpf } : {}),
    ...(typeof window !== "undefined" && window.location?.href
      ? { origin_url: window.location.href }
      : {}),
    accountId: a.accountId,
    siteId: a.siteId,
    channel: "Site",
    product: a.product?.trim() || a.model?.name || "Test Drive",
    mailing: a.contact.preferences.includes("email"),
    phoning: a.contact.preferences.includes("phone"),
    whatsapping: a.contact.preferences.includes("whatsapp"),
    ...(a.afVisitorId ? { af_visitor_id: a.afVisitorId } : {}),
    ...(a.antiBotToken ? { antiBotToken: a.antiBotToken } : {}),
    ...(contentEntryId ? { content_entry_id: contentEntryId } : {}),
    test_drive: {
      unitId: a.unit.id,
      ...(a.model?.id ? { modelId: a.model.id } : {}),
      ...(a.model?.name ? { modelName: a.model.name } : {}),
      ...(a.version?.id ? { versionId: a.version.id } : {}),
      ...(a.version?.name ? { versionName: a.version.name } : {}),
      date: a.date,
      timeSlot: a.timeSlot,
    },
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)

  const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
  const nested = (body?.data ?? {}) as Record<string, unknown>
  const protocol = nested.testDriveProtocol ?? body.testDriveProtocol
  return { ok: true, protocol: typeof protocol === "string" ? protocol : undefined }
}
